import type React from 'react'
import { useMemo, useState } from 'react'
import { Card, Empty, Radio, Typography } from 'antd'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis
} from 'recharts'
import { buildSeries, stripReserved, diffKeys } from './benchmarkSeries'

const { Text } = Typography

interface Props {
  benchmark: any
  config: any
  prefix: string
}

const TIMEFRAMES = [
  { key: '1h', hours: 1 },
  { key: '24h', hours: 24 },
  { key: '7d', hours: 24 * 7 },
  { key: '30d', hours: 24 * 30 }
]

const PROXY_COLORS = ['#1677ff', '#13c2c2', '#fa8c16', '#cf1322', '#722ed1', '#52c41a', '#eb2f96', '#fadb14', '#a0d911', '#f5222d']

const formatCount = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 10_000) return `${Math.round(n / 1000)}K`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return String(n)
}

const formatTime = (ts: number, hours: number): string => {
  // Guard: undefined/NaN ts (shape drift in benchmark payload or stale bundle)
  // must degrade to a dash instead of crashing on Date formatting downstream.
  if (!Number.isFinite(ts)) return '—'
  const d = new Date(ts)
  const pad = (n: number): string => String(n).padStart(2, '0')
  if (hours <= 24) return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  if (hours <= 24 * 7) return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`
}

const rateColor = (r: number | null): string => {
  if (r === null) return '#bfbfbf'
  if (r >= 90) return '#389e0d'
  if (r >= 50) return '#d48806'
  return '#cf1322'
}

export const ProxyBreakdownChart: React.FC<Props> = ({ benchmark, config, prefix }) => {
  const [hours, setHours] = useState<number>(24)
  const [mode, setMode] = useState<'proxy' | 'instance'>('proxy')
  const MAX_INSTANCES = 12

  const { chartData, proxies, summary, anyData, hiddenCount } = useMemo(() => {
    let successByKey: Record<string, Record<string, number>>
    let failByKey: Record<string, Record<string, number>>

    const emptyNested: Record<string, Record<string, number>> = {}
    if (mode === 'instance') {
      successByKey = stripReserved(benchmark?.[`success-${prefix}`] ?? emptyNested) as Record<string, Record<string, number>>
      failByKey = stripReserved(benchmark?.[`fail-${prefix}`] ?? emptyNested) as Record<string, Record<string, number>>
    } else {
      successByKey = benchmark?.[`success-${prefix}`]?.byProxy ?? emptyNested
      failByKey = benchmark?.[`fail-${prefix}`]?.byProxy ?? emptyNested
    }

    const { chartData, keys, summary } = buildSeries({ successByKey, failByKey, hours, now: Date.now() })

    if (mode === 'instance' && keys.length > MAX_INSTANCES) {
      const shown = keys.slice(0, MAX_INSTANCES)
      const shownSet = new Set(shown)
      return {
        chartData,
        proxies: shown,
        summary: summary.filter(x => shownSet.has(x.key)),
        anyData: shown.length > 0,
        hiddenCount: keys.length - shown.length
      }
    }

    return { chartData, proxies: keys, summary, anyData: keys.length > 0, hiddenCount: 0 }
  }, [benchmark, prefix, hours, mode])

  const { envByKey, diffSet } = useMemo(() => {
    if (mode !== 'instance') {
      const emptyEnv: Record<string, any> = {}
      return { envByKey: emptyEnv, diffSet: new Set<string>() }
    }
    const envByKey: Record<string, any> = {}
    for (const name of proxies) {
      const c = config?.[name]
      const env = c?.env ?? {}
      envByKey[name] = c?.scanInterval !== undefined ? { ...env, scanInterval: String(c.scanInterval) } : { ...env }
    }
    const diffSet = diffKeys(proxies.map(n => envByKey[n]))
    return { envByKey, diffSet }
  }, [mode, proxies, config])

  const renderBadge = (k: string, v: any): React.ReactNode => {
    const highlighted = diffSet.has(k)
    return (
      <span
        key={k}
        style={{
          fontSize: 11,
          borderRadius: 4,
          padding: '1px 6px',
          background: highlighted ? '#e6f4ff' : '#fafafa',
          color: highlighted ? '#0958d9' : '#595959',
          border: `1px solid ${highlighted ? '#91caff' : '#f0f0f0'}`,
          fontWeight: highlighted ? 600 : 400
        }}
      >
        {k} <span style={{ opacity: 0.85 }}>{String(v)}</span>
      </span>
    )
  }

  return (
    <div style={{ width: 860 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <Text style={{ fontSize: 16, fontWeight: 600 }}>
          Success rate {mode === 'instance' ? 'per istanza' : 'per proxy'} nel tempo
        </Text>
        <div style={{ display: 'flex', gap: 8 }}>
          <Radio.Group
            size='middle'
            value={mode}
            onChange={(e) => { setMode(e.target.value) }}
            options={[{ label: 'Per proxy', value: 'proxy' }, { label: 'Per istanza', value: 'instance' }]}
            optionType='button'
          />
          <Radio.Group
            size='middle'
            value={hours}
            onChange={(e) => { setHours(e.target.value) }}
            options={TIMEFRAMES.map(t => ({ label: t.key, value: t.hours }))}
            optionType='button'
          />
        </div>
      </div>

      {!anyData && (
        <Card size='small' bodyStyle={{ padding: 30 }}>
          <Empty description={mode === 'instance' ? 'Nessun dato per-istanza nel periodo selezionato' : 'Nessun dato byProxy nel periodo selezionato'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Card>
      )}
      {anyData && (
        <>
          <Card size='small' bodyStyle={{ padding: '12px 8px 4px 4px' }} style={{ marginBottom: 12 }}>
            <div style={{ height: 340 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray='3 3' stroke='#eee' />
                  <XAxis
                    dataKey='ts'
                    type='number'
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={(v: any) => formatTime(Number(v) || 0, hours)}
                    fontSize={13}
                    stroke='#595959'
                    tickMargin={6}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tickFormatter={(v: any) => `${String(v)}%`}
                    fontSize={13}
                    stroke='#595959'
                    tickMargin={4}
                  />
                  <ReferenceLine y={90} stroke='#52c41a' strokeDasharray='4 4' />
                  <ReferenceLine y={50} stroke='#ff4d4f' strokeDasharray='4 4' />
                  <RTooltip
                    cursor={{ stroke: '#bfbfbf', strokeDasharray: '3 3' }}
                    content={({ payload, label }: any) => {
                      if (!payload?.length) return null
                      const ts = Number(label) || 0
                      const rows = proxies.map((p, i) => {
                        const point = payload[0].payload
                        const rate: number | null = point[`${p}__rate`]
                        const vol: number = point[`${p}__vol`] ?? 0
                        const s: number = point[`${p}__s`] ?? 0
                        const f: number = point[`${p}__f`] ?? 0
                        if (vol === 0) return null
                        return (
                          <div key={p} style={{ display: 'flex', alignItems: 'center', fontSize: 14, marginBottom: 4 }}>
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                background: PROXY_COLORS[i % PROXY_COLORS.length],
                                borderRadius: '50%',
                                marginRight: 10
                              }}
                            />
                            <span style={{ minWidth: 86, fontWeight: 500 }}>
                              {p}
                              {mode === 'instance' && envByKey[p] && (
                                <span style={{ opacity: 0.6, fontWeight: 400, marginLeft: 4 }}>
                                  {[envByKey[p].PROXY, envByKey[p].CHROME, envByKey[p].FINGERPRINT].filter(Boolean).join(' · ')}
                                </span>
                              )}
                            </span>
                            <span style={{ marginLeft: 8, color: rateColor(rate), fontWeight: 700, minWidth: 50 }}>
                              {rate !== null ? `${rate.toFixed(0)}%` : '—'}
                            </span>
                            <span style={{ marginLeft: 10, opacity: 0.65, fontSize: 13 }}>
                              {formatCount(vol)} <span style={{ opacity: 0.75 }}>(OK {s} · KO {f})</span>
                            </span>
                          </div>
                        )
                      })
                      return (
                        <div
                          style={{
                            background: '#fff',
                            color: 'rgba(0,0,0,0.88)',
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            padding: '12px 14px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.10)'
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
                            {new Date(ts).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
                          </div>
                          {rows}
                        </div>
                      )
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 14, paddingTop: 8 }} iconType='circle' />
                  {proxies.map((p, i) => (
                    <Line
                      key={p}
                      type='monotone'
                      dataKey={`${p}__rate`}
                      name={p}
                      stroke={PROXY_COLORS[i % PROXY_COLORS.length]}
                      strokeWidth={2.2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {mode === 'instance' && hiddenCount > 0 && (
            <Text style={{ display: 'block', fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
              +{hiddenCount} istanze sotto soglia non mostrate (top {MAX_INSTANCES} per volume)
            </Text>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {summary.filter(x => x.total > 0).map((x) => {
              const i = proxies.indexOf(x.key)
              const color = PROXY_COLORS[i % PROXY_COLORS.length]
              return (
                <Card
                  key={x.key}
                  size='small'
                  bodyStyle={{ padding: '12px 14px' }}
                  style={{ borderLeft: `4px solid ${color}` }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: color,
                        marginRight: 8
                      }}
                    />
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{x.key}</span>
                  </div>
                  {mode === 'instance' && envByKey[x.key] && (
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                      {Object.keys(envByKey[x.key]).sort().map(k => renderBadge(k, envByKey[x.key][k]))}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 24, fontWeight: 700, color: rateColor(x.rate), fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                      {x.rate !== null ? `${x.rate.toFixed(0)}%` : '—'}
                    </span>
                    <span style={{ fontSize: 13, color: '#8c8c8c', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCount(x.total)} req
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                    OK {formatCount(x.success)} · KO {formatCount(x.fail)}
                  </div>
                </Card>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
